import React, { useState, useEffect, useRef, useCallback } from 'react';
import { playPhaseStart, playWarning, playTimeUp, playClick, playVictory, playRoundStart } from '../sounds';

const TYPE_LABELS = {
  whoMostLikely: { step: 'מי הכי...', icon: '🎯' },
  wouldYouRather: { step: 'מה מעדיפים?', icon: '⚖️' },
  guessWho: { step: 'מי כאן?', icon: '🔍' },
  hotTakes: { step: 'דעה חמה!', icon: '🔥' },
  drawIt: { step: 'ציור!', icon: '🎨' },
  completeIt: { step: 'השלם את המשפט!', icon: '✍️' },
};

const COLORS = ['#000000', '#FF3B30', '#007AFF', '#34C759', '#FF9500', '#AF52DE', '#FF2D55', '#FFFFFF'];

// Funny reaction messages based on results
const FUNNY_MESSAGES = {
  // When everyone votes for the same person in "who most likely"
  unanimous_who: [
    (name) => `${name}, אין מה לעשות - כולם מסכימים על זה`,
    (name) => `${name}, אם כולם חושבים ככה... אולי יש בזה משהו?`,
    (name) => `${name}, ברור שזה אתה - אפילו לא היה ויכוח`,
    (name) => `פה אחד! ${name}, תקבל/י את זה בספורטיביות`,
    (name) => `${name} - השם הראשון שעולה לכולם בראש`,
  ],
  // When someone gets a lot of votes (but not all)
  popular_who: [
    (name, q) => `${name}, ברור לכולם שזה אתה`,
    (name) => `${name}, אתה פשוט קלאסיקה`,
    (name) => `${name} - מפורסם/ת בקבוצה`,
    (name) => `${name}, מה נגיד, יש לך מוניטין`,
  ],
  // When votes are split evenly
  split_who: [
    () => `הקבוצה חלוקה! מעניין..`,
    () => `אין הסכמה - כנראה שכולכם כאלה`,
    () => `קשה להחליט, מה? כולכם מתאימים`,
  ],
  // Would you rather - unanimous
  unanimous_wyr: [
    () => `פה אחד! הקבוצה הזאת חושבת אותו דבר`,
    () => `כולכם אותו דבר בדיוק, מפחיד!`,
    () => `אין פה אפילו אחד שחושב אחרת?`,
  ],
  // Would you rather - split
  split_wyr: [
    () => `חצי חצי! מלחמה אמיתית`,
    () => `הקבוצה מפולגת... כמו הכנסת`,
    () => `יש פה ויכוח רציני`,
  ],
  // Hot takes - all agree
  all_agree: [
    (s) => `כולם מסכימים! "${s}" - אין על מה לריב`,
    () => `הקבוצה הכי מאוחדת שיש`,
    () => `פה אחד - אפילו לא צריך דיון`,
  ],
  // Hot takes - all disagree
  all_disagree: [
    () => `כולם לא מסכימים - לפחות אתם מאוחדים בזה`,
    () => `לא! פשוט לא! - כולם`,
  ],
  // Hot takes - split
  split_hot: [
    () => `הנה הגענו לנקודה הרגישה...`,
    () => `פה מתחילים הויכוחים האמיתיים`,
    () => `חצי מסכימים חצי לא - קלאסי`,
  ],
  // Drawing winner
  draw_win: [
    (name) => `${name}, יש לך עתיד באמנות! (או שלא)`,
    (name) => `פיקאסו היה מתגאה, ${name}!`,
    (name) => `${name}, הציור הזה שווה מיליונים (בערך)`,
    (name) => `${name} - האמן/ית הרשמי/ת של הקבוצה`,
  ],
  // Completion winner
  comp_win: [
    (name) => `${name}, איזה ראש יצירתי!`,
    (name) => `${name}, אתה צריך לכתוב סטנדאפ`,
    (name) => `${name} - הקומיקאי/ת של הקבוצה`,
    (name) => `${name}, תכתוב ספר כבר`,
  ],
};

function getFunnyMessage(type, ...args) {
  const messages = FUNNY_MESSAGES[type];
  if (!messages || messages.length === 0) return null;
  const msg = messages[Math.floor(Math.random() * messages.length)];
  return msg(...args);
}

// Memoize to keep the same message during re-renders
const funnyCache = {};
function getCachedFunny(key, type, ...args) {
  if (!funnyCache[key]) {
    funnyCache[key] = getFunnyMessage(type, ...args);
  }
  return funnyCache[key];
}

function Timer({ seconds, warning }) {
  return (
    <div className="timer-container">
      <div className={`timer-ring ${warning ? 'warning' : ''}`}>{seconds}</div>
    </div>
  );
}

// ===== DRAWING CANVAS =====
function DrawCanvas({ onSubmit, disabled }) {
  const canvasRef = useRef(null);
  const [color, setColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(4);
  const [isDrawing, setIsDrawing] = useState(false);
  const [history, setHistory] = useState([]);
  const lastPos = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHistory([canvas.toDataURL()]);
  }, []);

  const getPos = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  const startDraw = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    setIsDrawing(true);
    const pos = getPos(e);
    lastPos.current = pos;
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, brushSize / 2, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  }, [disabled, getPos, brushSize, color]);

  const draw = useCallback((e) => {
    if (!isDrawing || disabled) return;
    e.preventDefault();
    const pos = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = color;
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPos.current = pos;
  }, [isDrawing, disabled, getPos, color, brushSize]);

  const endDraw = useCallback(() => {
    if (isDrawing) {
      setIsDrawing(false);
      lastPos.current = null;
      const canvas = canvasRef.current;
      if (canvas) {
        setHistory((prev) => [...prev.slice(-15), canvas.toDataURL()]);
      }
    }
  }, [isDrawing]);

  const undo = () => {
    if (history.length <= 1) return;
    const newHistory = history.slice(0, -1);
    setHistory(newHistory);
    const img = new Image();
    img.onload = () => {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      ctx.drawImage(img, 0, 0);
    };
    img.src = newHistory[newHistory.length - 1];
  };

  const clearCanvas = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHistory([canvasRef.current.toDataURL()]);
  };

  const submit = () => {
    const dataUrl = canvasRef.current.toDataURL('image/png', 0.6);
    onSubmit(dataUrl);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
      <canvas
        ref={canvasRef}
        width={300}
        height={300}
        style={{
          width: '100%', maxWidth: '300px', height: 'auto', aspectRatio: '1',
          borderRadius: '12px', border: '2px solid rgba(255,255,255,0.15)',
          touchAction: 'none', cursor: 'crosshair', background: '#fff',
        }}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />

      {/* Color picker */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {COLORS.map((c) => (
          <button
            key={c}
            onClick={() => setColor(c)}
            style={{
              width: '30px', height: '30px', borderRadius: '50%', background: c,
              border: color === c ? '3px solid #6C3CE1' : '2px solid rgba(255,255,255,0.3)',
              cursor: 'pointer', boxShadow: color === c ? '0 0 8px rgba(108,60,225,0.5)' : 'none',
            }}
          />
        ))}
      </div>

      {/* Brush size + tools */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button
          onClick={() => setBrushSize(3)}
          className={`btn btn-ghost`}
          style={{ padding: '6px 12px', fontSize: '0.8rem', opacity: brushSize === 3 ? 1 : 0.5 }}
        >דק</button>
        <button
          onClick={() => setBrushSize(8)}
          className={`btn btn-ghost`}
          style={{ padding: '6px 12px', fontSize: '0.8rem', opacity: brushSize === 8 ? 1 : 0.5 }}
        >בינוני</button>
        <button
          onClick={() => setBrushSize(16)}
          className={`btn btn-ghost`}
          style={{ padding: '6px 12px', fontSize: '0.8rem', opacity: brushSize === 16 ? 1 : 0.5 }}
        >עבה</button>
        <button onClick={undo} className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: '0.8rem' }}>
          ↩️ בטל
        </button>
        <button onClick={clearCanvas} className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: '0.8rem' }}>
          🗑️ נקה
        </button>
      </div>

      {!disabled && (
        <button className="btn btn-primary" onClick={submit} style={{ width: '100%', maxWidth: '300px' }}>
          שלח ציור! 🎨
        </button>
      )}
    </div>
  );
}

function Confetti() {
  const pieces = Array.from({ length: 40 }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 2,
    color: ['#FFD700', '#FF5252', '#00D4AA', '#6C3CE1', '#FF69B4', '#4CAF50'][i % 6],
    size: 6 + Math.random() * 6,
  }));
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 999, overflow: 'hidden' }}>
      {pieces.map((p, i) => (
        <div key={i} style={{
          position: 'absolute', top: '-10px', left: `${p.left}%`,
          width: `${p.size}px`, height: `${p.size}px`, background: p.color,
          borderRadius: Math.random() > 0.5 ? '50%' : '2px',
          animation: `confettiFall ${2 + Math.random() * 2}s ${p.delay}s ease-in forwards`,
        }} />
      ))}
      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

function ThatsYou({ gameState, playerId, sendMessage, error, clearError }) {
  const {
    phase,
    currentRound,
    totalRounds,
    roundType,
    hasAnswered,
    answeredCount,
    totalPlayers,
    players,
    phaseTimeLeft,
    // Question data
    question,
    optionA,
    optionB,
    statement,
    agreeLabel,
    disagreeLabel,
    // Draw data
    drawPrompt,
    hasSubmittedDrawing,
    submittedCount,
    galleryDrawings,
    hasVoted,
    // CompleteIt data
    completePrompt,
    hasSubmittedCompletion,
    completionEntries,
    hasVotedCompletion,
    completeResults,
    compWinnerName,
    // Results
    answers,
    voteCounts,
    winner,
    winnerName,
    countA,
    countB,
    agreeCount,
    disagreeCount,
    majority,
    isUnanimous,
    gameOver,
    awards,
    // Draw results
    drawResults,
    drawWinnerName,
    drawVotes,
  } = gameState;

  const [localTimer, setLocalTimer] = useState(phaseTimeLeft || 0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [selectedDrawVote, setSelectedDrawVote] = useState(null);
  const [completionText, setCompletionText] = useState('');
  const isHost = players.find((p) => p.id === playerId)?.isHost;
  const typeInfo = TYPE_LABELS[roundType] || { step: '', icon: '' };

  useEffect(() => {
    setLocalTimer(phaseTimeLeft || 0);
  }, [phaseTimeLeft, phase, currentRound]);

  useEffect(() => {
    if (phase === 'results') return;
    const interval = setInterval(() => {
      setLocalTimer((prev) => {
        const next = Math.max(0, prev - 1);
        if (next === 5 || next === 3) playWarning();
        if (next === 0 && prev > 0) playTimeUp();
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase, currentRound]);

  useEffect(() => {
    if (localTimer === 5 && navigator.vibrate) navigator.vibrate(100);
    if (localTimer === 0 && phase !== 'results' && navigator.vibrate) navigator.vibrate([100, 50, 100]);
  }, [localTimer, phase]);

  useEffect(() => {
    setSelectedAnswer(null);
    setSelectedDrawVote(null);
    setCompletionText('');
    // Clear funny message cache for new round
    Object.keys(funnyCache).forEach((k) => delete funnyCache[k]);
    // Sound on new round
    if (phase === 'results') {
      if (gameOver) playVictory();
    } else {
      playRoundStart();
    }
  }, [currentRound]);

  const submitAnswer = (answer) => {
    setSelectedAnswer(answer);
    playClick();
    sendMessage('THATSYOU_ANSWER', { answer });
  };

  const submitDrawing = (imageData) => {
    sendMessage('THATSYOU_DRAW', { imageData });
  };

  const submitDrawVote = (votedForId) => {
    setSelectedDrawVote(votedForId);
    sendMessage('THATSYOU_DRAW_VOTE', { votedForId });
  };

  const myAnswer = selectedAnswer;

  // ===== DRAWING PHASE =====
  if (phase === 'drawing' && roundType === 'drawIt') {
    return (
      <div className="card">
        <div className="status-bar">
          <span>שאלה {currentRound} מתוך {totalRounds}</span>
          <span className="category-badge">{typeInfo.icon} {typeInfo.step}</span>
        </div>

        <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '4px', height: '4px', marginBottom: '16px' }}>
          <div style={{
            width: `${(currentRound / totalRounds) * 100}%`,
            height: '100%', borderRadius: '4px',
            background: 'linear-gradient(90deg, #6C3CE1, #00D4AA)',
            transition: 'width 0.3s',
          }} />
        </div>

        <Timer seconds={localTimer} warning={localTimer <= 5} />

        <h2 style={{ textAlign: 'center', fontSize: '1.2rem', marginBottom: '16px', lineHeight: 1.5 }}>
          {drawPrompt}
        </h2>

        {hasSubmittedDrawing ? (
          <p className="waiting" style={{ marginTop: '16px' }}>
            שלחת ציור! ממתינים לשאר ({submittedCount}/{totalPlayers})
          </p>
        ) : (
          <DrawCanvas onSubmit={submitDrawing} disabled={hasSubmittedDrawing} />
        )}
      </div>
    );
  }

  // ===== GALLERY PHASE (vote on drawings) =====
  if (phase === 'gallery' && roundType === 'drawIt') {
    return (
      <div className="card">
        <div className="status-bar">
          <span>שאלה {currentRound} מתוך {totalRounds}</span>
          <span className="category-badge">{typeInfo.icon} הצביעו לציור הכי מצחיק!</span>
        </div>

        <Timer seconds={localTimer} warning={localTimer <= 5} />

        <h2 style={{ textAlign: 'center', fontSize: '1.1rem', marginBottom: '16px', color: '#00D4AA' }}>
          {drawPrompt}
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' }}>
          {galleryDrawings && galleryDrawings.map((d) => (
            <button
              key={d.id}
              onClick={() => !hasVoted && d.id !== playerId && submitDrawVote(d.id)}
              disabled={hasVoted || d.id === playerId}
              style={{
                padding: '4px', borderRadius: '12px', cursor: d.id === playerId ? 'default' : 'pointer',
                border: selectedDrawVote === d.id ? '3px solid #00D4AA' : '2px solid rgba(255,255,255,0.15)',
                background: selectedDrawVote === d.id ? 'rgba(0,212,170,0.15)' : 'rgba(255,255,255,0.04)',
                opacity: d.id === playerId ? 0.5 : 1,
                transition: 'all 0.2s',
              }}
            >
              <img
                src={d.image}
                alt="ציור"
                style={{ width: '100%', borderRadius: '8px', display: 'block' }}
              />
              {d.id === playerId && (
                <p style={{ fontSize: '0.7rem', color: '#9b8cbf', textAlign: 'center', margin: '4px 0 0' }}>הציור שלך</p>
              )}
            </button>
          ))}
        </div>

        {hasVoted && (
          <p className="waiting" style={{ marginTop: '16px' }}>
            הצבעת! ממתינים לשאר ({answeredCount}/{totalPlayers})
          </p>
        )}
      </div>
    );
  }

  // ===== WRITING PHASE (completeIt) =====
  if (phase === 'writing' && roundType === 'completeIt') {
    return (
      <div className="card">
        <div className="status-bar">
          <span>שאלה {currentRound} מתוך {totalRounds}</span>
          <span className="category-badge">{typeInfo.icon} {typeInfo.step}</span>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '4px', height: '4px', marginBottom: '16px' }}>
          <div style={{ width: `${(currentRound / totalRounds) * 100}%`, height: '100%', borderRadius: '4px', background: 'linear-gradient(90deg, #6C3CE1, #00D4AA)', transition: 'width 0.3s' }} />
        </div>
        <Timer seconds={localTimer} warning={localTimer <= 5} />
        <div style={{ textAlign: 'center', padding: '16px', marginBottom: '16px', background: 'rgba(108,60,225,0.1)', borderRadius: '16px', border: '1px solid rgba(108,60,225,0.2)' }}>
          <p style={{ fontSize: '0.8rem', color: '#6C3CE1', marginBottom: '6px' }}>✍️ השלימו את המשפט</p>
          <h2 style={{ fontSize: '1.15rem', lineHeight: 1.6 }}>{completePrompt}</h2>
        </div>
        {hasSubmittedCompletion ? (
          <p className="waiting">נשלח! ממתינים לשאר ({answeredCount}/{totalPlayers})</p>
        ) : (
          <>
            <input
              className="input"
              placeholder="כתבו את ההשלמה שלכם..."
              value={completionText}
              onChange={(e) => setCompletionText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && completionText.trim() && sendMessage('THATSYOU_COMPLETE', { text: completionText.trim() })}
              maxLength={100}
              autoFocus
            />
            <button
              className="btn btn-primary"
              onClick={() => { if (completionText.trim()) sendMessage('THATSYOU_COMPLETE', { text: completionText.trim() }); }}
              disabled={!completionText.trim()}
            >
              שלח! ✍️
            </button>
          </>
        )}
      </div>
    );
  }

  // ===== READING PHASE (completeIt voting) =====
  if (phase === 'reading' && roundType === 'completeIt') {
    return (
      <div className="card">
        <div className="status-bar">
          <span>שאלה {currentRound} מתוך {totalRounds}</span>
          <span className="category-badge">✍️ הצביעו להשלמה הכי מצחיקה!</span>
        </div>
        <Timer seconds={localTimer} warning={localTimer <= 5} />
        <p style={{ textAlign: 'center', color: '#9b8cbf', fontSize: '0.9rem', marginBottom: '12px' }}>{completePrompt}</p>
        <div style={{ display: 'grid', gap: '8px' }}>
          {completionEntries && completionEntries.map((entry) => (
            <button
              key={entry.id}
              onClick={() => !hasVotedCompletion && entry.id !== playerId && sendMessage('THATSYOU_COMPLETE_VOTE', { votedForId: entry.id })}
              disabled={hasVotedCompletion || entry.id === playerId}
              style={{
                padding: '14px 16px', borderRadius: '14px', textAlign: 'right', cursor: entry.id === playerId ? 'default' : 'pointer',
                border: '2px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)',
                color: 'white', fontSize: '1rem', fontFamily: 'Heebo, sans-serif',
                opacity: entry.id === playerId ? 0.5 : 1, transition: 'all 0.2s',
              }}
            >
              "{entry.text}"
              {entry.id === playerId && <span style={{ fontSize: '0.75rem', color: '#9b8cbf', display: 'block', marginTop: '4px' }}>ההשלמה שלך</span>}
            </button>
          ))}
        </div>
        {hasVotedCompletion && (
          <p className="waiting" style={{ marginTop: '12px' }}>הצבעת! ממתינים לשאר ({answeredCount}/{totalPlayers})</p>
        )}
      </div>
    );
  }

  // ===== QUESTION PHASE =====
  if (phase === 'question') {
    return (
      <div className="card">
        <div className="status-bar">
          <span>שאלה {currentRound} מתוך {totalRounds}</span>
          <span className="category-badge">{typeInfo.icon} {typeInfo.step}</span>
        </div>

        {/* Progress bar */}
        <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '4px', height: '4px', marginBottom: '16px' }}>
          <div style={{
            width: `${(currentRound / totalRounds) * 100}%`,
            height: '100%', borderRadius: '4px',
            background: 'linear-gradient(90deg, #6C3CE1, #00D4AA)',
            transition: 'width 0.3s',
          }} />
        </div>

        <Timer seconds={localTimer} warning={localTimer <= 5} />

        {/* WHO MOST LIKELY / GUESS WHO */}
        {(roundType === 'whoMostLikely' || roundType === 'guessWho') && (
          <>
            <h2 style={{ textAlign: 'center', fontSize: '1.3rem', marginBottom: '20px', lineHeight: 1.5 }}>
              {question}
            </h2>
            <div style={{ display: 'grid', gap: '8px' }}>
              {players.map((p) => (
                <button
                  key={p.id}
                  className={`vote-btn ${myAnswer === p.id ? 'selected' : ''}`}
                  onClick={() => !hasAnswered && submitAnswer(p.id)}
                  disabled={hasAnswered}
                >
                  {p.name}
                  {p.id === playerId && <span style={{ color: '#9b8cbf', fontSize: '0.8rem' }}> (אני)</span>}
                </button>
              ))}
            </div>
          </>
        )}

        {/* WOULD YOU RATHER */}
        {roundType === 'wouldYouRather' && (
          <>
            <h2 style={{ textAlign: 'center', fontSize: '1.2rem', marginBottom: '20px', color: '#00D4AA' }}>
              מה אתם מעדיפים?
            </h2>
            <div style={{ display: 'grid', gap: '12px' }}>
              <button
                className={`option-btn ${myAnswer === 'a' ? 'selected' : ''}`}
                onClick={() => !hasAnswered && submitAnswer('a')}
                disabled={hasAnswered}
                style={{ padding: '20px', fontSize: '1.05rem', gridColumn: '1 / -1' }}
              >
                {optionA}
              </button>
              <div style={{ textAlign: 'center', color: '#9b8cbf', fontSize: '0.9rem' }}>או</div>
              <button
                className={`option-btn ${myAnswer === 'b' ? 'selected' : ''}`}
                onClick={() => !hasAnswered && submitAnswer('b')}
                disabled={hasAnswered}
                style={{ padding: '20px', fontSize: '1.05rem', gridColumn: '1 / -1' }}
              >
                {optionB}
              </button>
            </div>
          </>
        )}

        {/* HOT TAKES */}
        {roundType === 'hotTakes' && (
          <>
            <div style={{
              textAlign: 'center', padding: '20px', marginBottom: '20px',
              background: 'rgba(255,82,82,0.08)', borderRadius: '16px',
              border: '1px solid rgba(255,82,82,0.15)',
            }}>
              <p style={{ fontSize: '0.8rem', color: '#FF5252', marginBottom: '8px' }}>🔥 דעה חמה</p>
              <h2 style={{ fontSize: '1.2rem', lineHeight: 1.5 }}>"{statement}"</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <button
                className={`option-btn ${myAnswer === 'agree' ? 'selected' : ''}`}
                onClick={() => !hasAnswered && submitAnswer('agree')}
                disabled={hasAnswered}
                style={{ padding: '18px', fontSize: '1.05rem', borderColor: myAnswer === 'agree' ? '#4CAF50' : undefined, background: myAnswer === 'agree' ? 'rgba(76,175,80,0.2)' : undefined }}
              >
                {agreeLabel || 'מסכים'}
              </button>
              <button
                className={`option-btn ${myAnswer === 'disagree' ? 'selected' : ''}`}
                onClick={() => !hasAnswered && submitAnswer('disagree')}
                disabled={hasAnswered}
                style={{ padding: '18px', fontSize: '1.05rem', borderColor: myAnswer === 'disagree' ? '#FF5252' : undefined, background: myAnswer === 'disagree' ? 'rgba(255,82,82,0.2)' : undefined }}
              >
                {disagreeLabel || 'לא מסכים'}
              </button>
            </div>
          </>
        )}

        {hasAnswered && (
          <p className="waiting" style={{ marginTop: '16px' }}>
            ענית! ממתינים לשאר ({answeredCount}/{totalPlayers})
          </p>
        )}
      </div>
    );
  }

  // ===== RESULTS PHASE =====
  if (phase === 'results') {
    const sortedPlayers = [...players].sort((a, b) => b.score - a.score);

    return (
      <div className="card">
        <div className="status-bar">
          <span>שאלה {currentRound} מתוך {totalRounds}</span>
          <span className="category-badge">{typeInfo.icon} {typeInfo.step}</span>
        </div>

        {gameOver && <Confetti />}

        {gameOver && (
          <div className="game-over-title">המשחק נגמר!</div>
        )}

        {/* WHO MOST LIKELY / GUESS WHO results */}
        {(roundType === 'whoMostLikely' || roundType === 'guessWho') && (
          <div style={{ marginBottom: '20px' }}>
            <p style={{ textAlign: 'center', color: '#9b8cbf', fontSize: '0.9rem', marginBottom: '8px' }}>
              {question}
            </p>
            {winnerName && (() => {
              const totalVotes = Object.keys(answers || {}).length;
              const winnerVotes = voteCounts?.[winner] || 0;
              const isAllVotes = winnerVotes >= totalVotes && totalVotes > 1;
              const isMajority = winnerVotes > totalVotes / 2;
              const funnyKey = `who_${currentRound}`;
              const funny = isAllVotes
                ? getCachedFunny(funnyKey, 'unanimous_who', winnerName)
                : isMajority
                  ? getCachedFunny(funnyKey, 'popular_who', winnerName, question)
                  : getCachedFunny(funnyKey, 'split_who');
              return (
                <>
                  <div style={{
                    textAlign: 'center', padding: '20px',
                    background: 'linear-gradient(135deg, rgba(124,77,255,0.2), rgba(0,229,160,0.08))',
                    borderRadius: '18px', border: '1px solid rgba(124,77,255,0.25)',
                    animation: 'bounceIn 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                    marginBottom: '12px',
                  }}>
                    <span style={{ fontSize: '2rem', display: 'block', marginBottom: '6px' }}>🏆</span>
                    <h2 style={{ color: '#00E5A0', fontSize: '1.4rem' }}>{winnerName}</h2>
                    <p style={{ color: '#a099c2', fontSize: '0.85rem', marginTop: '4px' }}>
                      {winnerVotes} הצבעות
                    </p>
                  </div>
                  {funny && (
                    <p style={{
                      textAlign: 'center', color: '#FFD740', fontSize: '0.88rem',
                      fontStyle: 'italic', marginBottom: '12px',
                      animation: 'fadeIn 0.5s 0.3s both',
                    }}>
                      "{funny}"
                    </p>
                  )}
                </>
              );
            })()}
            {/* Show all votes */}
            {players.map((p) => {
              const count = voteCounts?.[p.id] || 0;
              const pct = totalPlayers > 0 ? (count / Object.keys(answers || {}).length) * 100 : 0;
              const voters = answers
                ? Object.entries(answers)
                    .filter(([, a]) => a.answer === p.id)
                    .map(([, a]) => a.name)
                : [];
              return (
                <div key={p.id} style={{ marginBottom: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '3px' }}>
                    <span>{p.name} {p.id === winner && '🏆'}</span>
                    <span style={{ color: '#9b8cbf' }}>{count} הצבעות</span>
                  </div>
                  <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '4px', height: '6px', marginBottom: '3px' }}>
                    <div style={{
                      width: `${pct}%`, height: '100%', borderRadius: '4px',
                      background: p.id === winner ? '#00D4AA' : '#6C3CE1',
                      transition: 'width 0.5s',
                    }} />
                  </div>
                  {voters.length > 0 && (
                    <p style={{ fontSize: '0.75rem', color: '#9b8cbf', marginTop: '3px' }}>
                      הצביעו: {voters.join(', ')}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* WOULD YOU RATHER results */}
        {roundType === 'wouldYouRather' && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
              <div style={{
                padding: '16px', borderRadius: '14px', textAlign: 'center',
                background: majority === 'a' ? 'rgba(0,212,170,0.15)' : 'rgba(255,255,255,0.04)',
                border: majority === 'a' ? '2px solid #00D4AA' : '2px solid transparent',
              }}>
                <p style={{ fontSize: '0.95rem', marginBottom: '8px' }}>{optionA}</p>
                <span style={{ fontSize: '1.8rem', fontWeight: 900, color: '#00D4AA' }}>{countA}</span>
              </div>
              <div style={{
                padding: '16px', borderRadius: '14px', textAlign: 'center',
                background: majority === 'b' ? 'rgba(108,60,225,0.15)' : 'rgba(255,255,255,0.04)',
                border: majority === 'b' ? '2px solid #6C3CE1' : '2px solid transparent',
              }}>
                <p style={{ fontSize: '0.95rem', marginBottom: '8px' }}>{optionB}</p>
                <span style={{ fontSize: '1.8rem', fontWeight: 900, color: '#6C3CE1' }}>{countB}</span>
              </div>
            </div>
            {(() => {
              const funnyKey = `wyr_${currentRound}`;
              const funny = isUnanimous
                ? getCachedFunny(funnyKey, 'unanimous_wyr')
                : (countA === countB)
                  ? getCachedFunny(funnyKey, 'split_wyr')
                  : null;
              return funny ? (
                <p style={{
                  textAlign: 'center', color: '#FFD740', fontSize: '0.88rem',
                  fontStyle: 'italic', marginBottom: '8px',
                  animation: 'fadeIn 0.5s 0.3s both',
                }}>
                  "{funny}"
                </p>
              ) : isUnanimous ? (
                <p style={{ textAlign: 'center', color: '#FFD700', fontWeight: 700 }}>פה אחד! 🎉</p>
              ) : null;
            })()}
            {/* Who answered what */}
            <div style={{ fontSize: '0.85rem', color: '#a099c2', marginTop: '8px' }}>
              {answers && Object.entries(answers).map(([pid, { name, answer }]) => (
                <span key={pid} style={{
                  display: 'inline-block', margin: '3px',
                  padding: '3px 10px', borderRadius: '12px',
                  background: answer === 'a' ? 'rgba(0,212,170,0.1)' : 'rgba(108,60,225,0.1)',
                  border: `1px solid ${answer === 'a' ? 'rgba(0,212,170,0.2)' : 'rgba(108,60,225,0.2)'}`,
                }}>
                  {name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* HOT TAKES results */}
        {roundType === 'hotTakes' && (
          <div style={{ marginBottom: '20px' }}>
            <p style={{ textAlign: 'center', color: '#9b8cbf', fontSize: '0.9rem', marginBottom: '12px' }}>
              "{statement}"
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
              <div style={{
                padding: '16px', borderRadius: '14px', textAlign: 'center',
                background: majority === 'agree' ? 'rgba(76,175,80,0.15)' : 'rgba(255,255,255,0.04)',
                border: majority === 'agree' ? '2px solid #4CAF50' : '2px solid transparent',
              }}>
                <p style={{ fontSize: '0.9rem', marginBottom: '6px' }}>{agreeLabel || 'מסכים'}</p>
                <span style={{ fontSize: '1.8rem', fontWeight: 900, color: '#4CAF50' }}>{agreeCount}</span>
              </div>
              <div style={{
                padding: '16px', borderRadius: '14px', textAlign: 'center',
                background: majority === 'disagree' ? 'rgba(255,82,82,0.15)' : 'rgba(255,255,255,0.04)',
                border: majority === 'disagree' ? '2px solid #FF5252' : '2px solid transparent',
              }}>
                <p style={{ fontSize: '0.9rem', marginBottom: '6px' }}>{disagreeLabel || 'לא מסכים'}</p>
                <span style={{ fontSize: '1.8rem', fontWeight: 900, color: '#FF5252' }}>{disagreeCount}</span>
              </div>
            </div>
            {(() => {
              const funnyKey = `hot_${currentRound}`;
              const funny = isUnanimous
                ? (majority === 'agree'
                    ? getCachedFunny(funnyKey, 'all_agree', statement)
                    : getCachedFunny(funnyKey, 'all_disagree'))
                : (agreeCount === disagreeCount)
                  ? getCachedFunny(funnyKey, 'split_hot')
                  : null;
              return funny ? (
                <p style={{
                  textAlign: 'center', color: '#FFD740', fontSize: '0.88rem',
                  fontStyle: 'italic', marginBottom: '8px',
                  animation: 'fadeIn 0.5s 0.3s both',
                }}>
                  "{funny}"
                </p>
              ) : isUnanimous ? (
                <p style={{ textAlign: 'center', color: '#FFD700', fontWeight: 700 }}>כולם חושבים אותו דבר! 🤯</p>
              ) : null;
            })()}
            {answers && Object.entries(answers).map(([pid, { name, answer }]) => (
              <span key={pid} style={{
                display: 'inline-block', margin: '3px',
                padding: '3px 10px', borderRadius: '12px', fontSize: '0.85rem',
                background: answer === 'agree' ? 'rgba(76,175,80,0.1)' : 'rgba(255,82,82,0.1)',
                border: `1px solid ${answer === 'agree' ? 'rgba(76,175,80,0.2)' : 'rgba(255,82,82,0.2)'}`,
              }}>
                {name}
              </span>
            ))}
          </div>
        )}

        {/* DRAW IT results */}
        {roundType === 'drawIt' && drawResults && (
          <div style={{ marginBottom: '20px' }}>
            <p style={{ textAlign: 'center', color: '#9b8cbf', fontSize: '0.9rem', marginBottom: '12px' }}>
              {drawPrompt}
            </p>
            {drawWinnerName && (
              <>
                <div style={{
                  textAlign: 'center', padding: '14px', marginBottom: '8px',
                  background: 'linear-gradient(135deg, rgba(124,77,255,0.2), rgba(0,229,160,0.08))',
                  borderRadius: '18px', border: '1px solid rgba(124,77,255,0.25)',
                  animation: 'bounceIn 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                }}>
                  <span style={{ fontSize: '1.5rem' }}>🎨🏆</span>
                  <h2 style={{ color: '#00E5A0', fontSize: '1.2rem' }}>{drawWinnerName}</h2>
                  <p style={{ color: '#a099c2', fontSize: '0.8rem' }}>הציור הכי מצחיק!</p>
                </div>
                <p style={{
                  textAlign: 'center', color: '#FFD740', fontSize: '0.88rem',
                  fontStyle: 'italic', marginBottom: '12px',
                  animation: 'fadeIn 0.5s 0.3s both',
                }}>
                  "{getCachedFunny(`draw_${currentRound}`, 'draw_win', drawWinnerName)}"
                </p>
              </>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px' }}>
              {drawResults.map((d) => (
                <div
                  key={d.playerId}
                  style={{
                    padding: '6px', borderRadius: '12px', textAlign: 'center',
                    background: d.isWinner ? 'rgba(0,212,170,0.1)' : 'rgba(255,255,255,0.04)',
                    border: d.isWinner ? '2px solid #00D4AA' : '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <img src={d.image} alt="ציור" style={{ width: '100%', borderRadius: '8px', marginBottom: '4px' }} />
                  <p style={{ fontSize: '0.8rem', fontWeight: 700 }}>{d.playerName}</p>
                  <p style={{ fontSize: '0.72rem', color: '#9b8cbf' }}>{d.votes} הצבעות {d.isWinner && '🏆'}</p>
                </div>
              ))}
            </div>
            {/* Show who voted for whom */}
            {drawVotes && Object.keys(drawVotes).length > 0 && (
              <div style={{ marginTop: '10px', fontSize: '0.78rem', color: '#9b8cbf' }}>
                {Object.values(drawVotes).map((v, i) => (
                  <span key={i} style={{
                    display: 'inline-block', margin: '2px', padding: '2px 8px', borderRadius: '10px',
                    background: 'rgba(108,60,225,0.1)', border: '1px solid rgba(108,60,225,0.2)',
                  }}>
                    {v.voterName} → {v.votedName}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* COMPLETE IT results */}
        {roundType === 'completeIt' && completeResults && (
          <div style={{ marginBottom: '20px' }}>
            <p style={{ textAlign: 'center', color: '#9b8cbf', fontSize: '0.9rem', marginBottom: '12px' }}>{completePrompt}</p>
            {compWinnerName && (
              <>
                <div style={{
                  textAlign: 'center', padding: '14px', marginBottom: '8px',
                  background: 'linear-gradient(135deg, rgba(124,77,255,0.2), rgba(0,229,160,0.08))',
                  borderRadius: '18px', border: '1px solid rgba(124,77,255,0.25)',
                  animation: 'bounceIn 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                }}>
                  <span style={{ fontSize: '1.5rem' }}>✍️🏆</span>
                  <h2 style={{ color: '#00E5A0', fontSize: '1.2rem' }}>{compWinnerName}</h2>
                  <p style={{ color: '#a099c2', fontSize: '0.8rem' }}>ההשלמה הכי מצחיקה!</p>
                </div>
                <p style={{
                  textAlign: 'center', color: '#FFD740', fontSize: '0.88rem',
                  fontStyle: 'italic', marginBottom: '12px',
                  animation: 'fadeIn 0.5s 0.3s both',
                }}>
                  "{getCachedFunny(`comp_${currentRound}`, 'comp_win', compWinnerName)}"
                </p>
              </>
            )}
            {completeResults.map((r, i) => (
              <div key={r.playerId} style={{
                padding: '12px 14px', borderRadius: '12px', marginBottom: '6px',
                background: r.isWinner ? 'rgba(0,212,170,0.1)' : 'rgba(255,255,255,0.04)',
                border: r.isWinner ? '2px solid #00D4AA' : '1px solid rgba(255,255,255,0.08)',
                animation: `slideUp 0.4s ${i * 0.15}s both`,
              }}>
                <p style={{ fontSize: '1rem', marginBottom: '4px' }}>"{r.text}"</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#9b8cbf' }}>
                  <span>{r.playerName} {r.isWinner && '🏆'}</span>
                  <span>{r.votes} הצבעות</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Awards on game over */}
        {gameOver && awards && awards.length > 0 && (
          <div className="result-section" style={{ marginBottom: '20px' }}>
            <h3>טקס פרסים 🏆</h3>
            {awards.map((award, i) => (
              <div key={i} className="player-item" style={{
                animation: `slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${i * 0.2}s both`,
                background: 'linear-gradient(135deg, rgba(255,215,0,0.08), rgba(255,215,0,0.02))',
                border: '1px solid rgba(255,215,0,0.15)',
                marginBottom: '8px',
              }}>
                <span style={{ fontSize: '1.5rem', marginLeft: '10px' }}>{award.icon}</span>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 700, color: '#FFD700' }}>{award.title}</p>
                  <p style={{ fontSize: '0.8rem', color: '#9b8cbf' }}>{award.playerName} - {award.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Leaderboard */}
        <div className="result-section">
          <h3>ניקוד</h3>
          <div className="leaderboard">
            {sortedPlayers.map((p, i) => (
              <div key={p.id} className="leaderboard-item">
                <span className="leaderboard-rank">
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`}
                </span>
                <span className="leaderboard-name">
                  {p.avatar && <span style={{ marginLeft: '4px' }}>{p.avatar}</span>} {p.name}
                  {p.id === playerId && <span className="me-badge"> (אני)</span>}
                </span>
                <span className="leaderboard-score">{p.score}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="scoring-legend">
          <strong>ניקוד:</strong> הצבעה עם הרוב = 3 נק' | דעה יחודית = 4-5 נק' | ראשון לענות = +1 נק' | ציור מנצח = 5 נק'
        </div>

        {error && <p className="error-msg">{error}</p>}

        {isHost && !gameOver && (
          <button className="btn btn-primary" onClick={() => { clearError(); sendMessage('NEXT_ROUND', {}); }}>
            שאלה הבאה
          </button>
        )}

        {isHost && gameOver && (
          <>
            <button className="btn btn-primary" onClick={() => { clearError(); sendMessage('START_GAME', {}); }}>
              שחקו שוב! 🔄
            </button>
            <button className="btn btn-secondary" onClick={() => { clearError(); sendMessage('NEW_GAME', {}); }}>
              חזרה ללובי
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => {
                const text = `🎉 בלי פילטר! - תוצאות\n\n${[...players].sort((a,b) => b.score - a.score).map((p, i) => `${i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i+1}.`} ${p.name}: ${p.score} נקודות`).join('\n')}\n\nשחקו גם: ${window.location.origin}`;
                navigator.clipboard.writeText(text).catch(() => {});
              }}
            >
              שתף תוצאות
            </button>
          </>
        )}

        {!isHost && !gameOver && (
          <p className="waiting">ממתינים לשאלה הבאה...</p>
        )}

        {!isHost && gameOver && (
          <p className="waiting">ממתינים שהמנהל יבחר מה לעשות...</p>
        )}
      </div>
    );
  }

  return (
    <div className="card">
      <p style={{ textAlign: 'center', color: '#9b8cbf' }}>טוען...</p>
    </div>
  );
}

export default ThatsYou;
